// Mini-app bootstrap: turn a Bale launch into a logged-in Hamyan session.
//
// Runs once, before the router renders, so nobody sees a login screen flash
// before the token lands. It is written to never throw and never block for
// long: every failure path falls through to the ordinary SMS login, which
// still works fine inside the mini-app webview.

import posthog from 'posthog-js'

import { miniappAuth, miniappContactLogin, miniappLink } from 'services/miniapp'
import {
    ensureSessionProfile,
    hasSession,
    isSupportSession,
} from 'utilities/session'
import { POST_LOGIN_PATH } from 'utilities/navigation'
import {
    describeLaunch,
    getInitData,
    getLinkTicket,
    getRememberedPlatform,
    rememberLinkTicket,
    resolvePlatform,
    isAutoLoginSuppressed,
    isMiniapp,
    markMiniappDocument,
    rememberPlatform,
} from './detect'
import { announceReady, deliversContactToBot } from './sdk'
import { resyncMiniappBackButton } from './backButton'
import { queueEvent, queueRegister } from 'utilities/analytics'
import {
    landingCodeFromStartParam,
    landingPath,
    rememberLandingAttribution,
} from 'modules/landing/code'
import { markFreshLogin } from './homeScreen'
import { notificationPath, reportNotificationOpen } from './notificationOpen'
import {
    inviteCodeFromStartParam,
    rememberInvite,
    reportPendingInvite,
} from './invite'
import type { MiniappPlatform } from './sdk'

export {
    describeLaunch,
    isMiniapp,
    getInitData,
    getLinkTicket,
    getPlatformHint,
    getStartParam,
    suppressAutoLogin,
    markMiniappDocument,
} from './detect'
export { loadSdk, announceReady, deliversContactToBot } from './sdk'
export { installMiniappBackButton } from './backButton'
export { resyncMiniappBackButton } from './backButton'
export {
    shouldOfferHomeScreen,
    addToHomeScreen,
    rememberDismissed,
} from './homeScreen'
export {
    requestPhoneFromMessenger,
    canRequestContact,
    toLocalMobile,
} from './contact'
export type { ContactResult } from './contact'

/**
 * Trade a signed shared contact for a session — the zero-SMS path.
 *
 * Returns true when the user is now logged in. Only Eitaa and Telegram can
 * reach here; Bale's unsigned number never gets this far.
 */
export const loginWithSharedContact = async (
    signedResponse: string
): Promise<boolean> => {
    const initData = getInitData()
    if (!initData) return false
    try {
        const result = await miniappContactLogin(initData, signedResponse)
        if (!result?.token) return false
        storeSession(result.token)
        markFreshLogin()
        posthog.capture('miniapp_contact_login', {
            platform: getRememberedPlatform(),
        })
        return true
    } catch {
        // Unsigned, mismatched or refused — the SMS login is still right there.
        posthog.capture('miniapp_contact_login_failed')
        return false
    }
}
export type { MiniappPlatform } from './sdk'

const storeSession = (token: string) => {
    window.localStorage.setItem('user', JSON.stringify({ token }))
}

/**
 * Complete the session and report where we should land, if not here.
 *
 * The profile guarantee itself lives in `utilities/session` because it is not
 * a mini-app concern — every entry point needs it. Only the routing choice
 * below is specific to launching inside a messenger.
 *
 * Returns a path for the caller to navigate to rather than rewriting history
 * directly: the router captures its location when `routes.tsx` is imported,
 * which happens before any of this runs, so a `replaceState` here would be
 * silently ignored.
 */
const completeSession = async (): Promise<string | null> => {
    const redirect = await ensureSessionProfile()
    if (redirect) return redirect

    // A stale token can bounce us onto /auth/login via the 401 interceptor.
    // Now that we hold a working session, don't leave the user staring at a
    // login form they don't need.
    if (window.location.pathname.startsWith('/auth')) return POST_LOGIN_PATH

    return null
}

/**
 * Where a notification link wants this launch to land, once signed in.
 *
 * Eitaa's link needs resolving (returns the path); Bale's button is already on
 * the page and only gets reported (returns null). See `notificationOpen.ts`.
 */
const openedFromNotification = async (): Promise<string | null> => {
    reportNotificationOpen()
    return notificationPath()
}

/**
 * The launch bookkeeping a returning user used to skip entirely.
 *
 * A user with a live session never reached `/miniapp/auth/`, and two things
 * depended on that call:
 *
 *   1. **The platform**, when the bridge sniff could not tell. The backend is
 *      the only authority: it reads it off whichever bot secret validates the
 *      signature. (This used to be the whole function, and ran only when the
 *      platform was unknown.)
 *   2. **Usage.** That call is what stamps the messenger account as seen, and
 *      the backend's messaging core reads the stamp to decide whether to
 *      message someone in their messenger or fall back to an SMS. With a
 *      30-day session, an active user looked idle for 30 days at a time.
 *   3. **The link itself.** Someone who signed in with a phone number inside
 *      the mini app when the link step never landed has a session here and NO
 *      messenger link — so they can never be messaged, and nothing ever
 *      retried. The answer says `linked: false` and hands over a ticket; with a
 *      session in hand that is all `/miniapp/link/` needs. No logout, no second
 *      login.
 *
 * It only ever CREATES a missing link. A messenger account linked to somebody
 * else is left alone — re-pointing stays what it was, the explicit act of
 * logging in with another number — and a support session must never bind the
 * support person's messenger to the member they are helping.
 *
 * Background and silent: nothing on screen waits for it, and a failure leaves
 * everything exactly as it was before this existed.
 */
const refreshLaunch = async (
    initData: string,
    known: MiniappPlatform | null
): Promise<void> => {
    try {
        const result = await miniappAuth(initData)
        if (result.platform && result.platform !== known) {
            rememberPlatform(result.platform)
            queueRegister({ miniapp_platform: result.platform })
            announceReady(result.platform)
            // The back button was installed while the platform was still
            // unknown, so it is sitting there unclaimed. Now it can be claimed.
            resyncMiniappBackButton()
        }
        if (result.linked || !result.link_ticket || isSupportSession()) return
        await miniappLink({ linkTicket: result.link_ticket })
        queueEvent('miniapp_silent_link', {
            platform: result.platform ?? known,
        })
    } catch {
        // Without a platform this install never registers `miniapp_platform`,
        // so every event it sends from here on is missing the super-property
        // that makes a messenger-specific failure visible at all.
        if (!known) queueEvent('miniapp_platform_unknown', {})
    }
}

/**
 * Auto-login for a mini-app launch. Safe to call on every page load.
 *
 * Resolves once we either have a session or know we cannot get one silently;
 * the caller renders the app either way. Resolves to a path the caller should
 * navigate to after rendering, or null to stay put.
 */
export const bootstrapMiniapp = async (): Promise<string | null> => {
    const initData = getInitData()
    if (!initData) return null

    // Before anything renders: lets CSS drop the bottom inset the webview
    // reports but does not need.
    markMiniappDocument()

    // Registered (not just captured) so EVERY later event carries which
    // messenger, client and SDK version it came from — otherwise a
    // platform-specific failure is invisible in aggregate.
    const launch = describeLaunch()
    queueRegister(launch)
    queueEvent('miniapp_launch', launch)

    // A campaign launch (`?startapp=q_<code>`) was tapped from a specific
    // invitation, so the page that invitation promised — not the app's own
    // front door — is where this person expects to land.
    //
    // Resolved here, before any of the early returns below, for two reasons:
    // the fragment the code may be sitting in is destroyed by the first
    // client-side navigation, and the attribution has to be registered whether
    // or not auto-login succeeds. A person who never signs in is still someone
    // we invited and still someone who looked.
    const landingCode = landingCodeFromStartParam()
    const landing = landingCode ? landingPath(landingCode) : null
    if (landingCode) {
        rememberLandingAttribution(landingCode, 'miniapp')
        queueEvent('lottery_landing_launched', { landing_code: landingCode })
    }
    // A manager's invite (`?startapp=i_<code>`): kept until there is a session
    // to report it with — see `invite.ts`.
    const inviteCode = inviteCodeFromStartParam()
    if (inviteCode) {
        rememberInvite(inviteCode)
        queueEvent('miniapp_invite_opened', { invite_code: inviteCode })
    }

    // Work the platform out before the auth round-trip, so the client's spinner
    // can be dismissed immediately — and, more importantly, so the paths below
    // that return early still end up with an SDK. `resolvePlatform` asks the
    // injected native bridge first, which answers instantly and works on a
    // launch this install has never made before.
    const known = resolvePlatform()
    if (known) {
        rememberPlatform(known)
        queueRegister({ miniapp_platform: known })
        announceReady(known)
    }

    // They signed out on purpose in this tab; signing them back in would make
    // the button look broken.
    if (isAutoLoginSuppressed()) {
        queueEvent('miniapp_auto_login_suppressed', { platform: known })
        // Signed out on purpose, but the landing is a public page — refusing
        // to show it would make their own invitation link look broken.
        return landing
    }

    // Already signed in. Still fall through to the profile check below: an
    // earlier build stored the token without ever writing `profile`, and those
    // sessions look healthy right up until the sidebar logs them out.
    if (hasSession()) {
        // ...but first do the bookkeeping this shortcut used to skip: learn the
        // platform when the bridge sniff could not, let the backend see that
        // this messenger account is in use, and create the link if an earlier
        // phone-number login never got one. Skipping auth here is what stranded
        // every returning user without an SDK — and, less visibly, what made
        // an active user look idle for the 30 days their session lasted. One
        // background request per launch; see `refreshLaunch`.
        // The invite is reported after the launch bookkeeping, which may be
        // what links this account — and «آمد» is decided on that link.
        void refreshLaunch(initData, known).finally(() => {
            void reportPendingInvite()
        })
        // The branch that swallowed most launches: `miniapp_launch` counted
        // every open, but only the two login outcomes below reported anything,
        // so a returning user was a launch with no ending. This is the ending —
        // and the commonest one.
        queueEvent('miniapp_session_resumed', {
            platform: known ?? getRememberedPlatform(),
            platform_known: !!known,
        })
        // `completeSession` wins when it answers: an incomplete profile breaks
        // a dozen screens, the landing included, so finishing it comes first.
        return (
            (await completeSession()) ??
            landing ??
            (await openedFromNotification())
        )
    }

    try {
        const result = await miniappAuth(initData)

        // The backend identified the messenger from the payload signature;
        // trust that over anything we could sniff client-side.
        const platform = (result.platform || known) as MiniappPlatform | null
        if (platform && platform !== known) {
            rememberPlatform(platform)
            queueRegister({ miniapp_platform: platform })
            announceReady(platform)
        }

        if (result.linked && result.token) {
            storeSession(result.token)
            // First launch of a real session: the moment the add-to-home-screen
            // nudge is allowed to appear.
            markFreshLogin()
            queueEvent('miniapp_auto_login', { platform })
            void reportPendingInvite()
            return (
                (await completeSession()) ??
                landing ??
                (await openedFromNotification())
            )
        }

        // Keep the launch proof for after the SMS login. On Eitaa this is the
        // only way the account can ever be bound, since its verification
        // cannot be repeated for the same launch.
        if (result.link_ticket) rememberLinkTicket(result.link_ticket)

        queueEvent('miniapp_login_required', {
            platform,
            reason: result.requires_2fa ? '2fa' : 'not_linked',
        })
    } catch {
        // Forged/expired payload, or the backend is unreachable. The SMS login
        // is a complete fallback, so there is nothing to report to the user.
        queueEvent('miniapp_auto_login_failed', { platform: known })
    }

    // Not signed in — which for a campaign visitor is the normal case, and
    // exactly why the landing has to render without a session.
    return landing
}

/**
 * Wait for the bot's webhook to link this account, then sign in.
 *
 * Bale delivers a mini-app `requestContact()` to the BOT — the contact lands in
 * the chat and reaches our webhook server-to-server, which links the account.
 * (Its docs say the number goes only to the page; observed behaviour says
 * otherwise, and the webhook log proves it.) That is the trustworthy channel,
 * but it completes *behind* the page, so nothing tells the mini app it happened.
 *
 * So after asking for the contact we poll our own auth endpoint for a few
 * seconds. The moment the link exists we get a token back and the user is in —
 * no SMS. If it never appears we simply stop, and the SMS login is untouched.
 */
export const waitForContactLink = async (
    attempts = 8,
    delayMs = 1500
): Promise<boolean> => {
    const initData = getInitData()
    if (!initData) return false

    // Skip entirely where nothing can arrive. Eitaa has no bot webhook, so
    // polling there is a guaranteed-useless wait — which is exactly what made
    // its button look broken: twelve silent seconds and no outcome.
    const platform = resolvePlatform()
    if (attempts > 1 && platform && !deliversContactToBot(platform)) {
        posthog.capture('miniapp_contact_webhook_skipped', {
            platform,
            reason: 'no_bot_webhook',
        })
        return false
    }

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs))
        try {
            const result = await miniappAuth(initData)
            if (result.linked && result.token) {
                storeSession(result.token)
                markFreshLogin()
                posthog.capture('miniapp_contact_webhook_login', {
                    platform: getRememberedPlatform(),
                })
                return true
            }
        } catch {
            // Transient; keep waiting for the webhook rather than giving up.
        }
    }

    // Falling out of the loop is the zero-SMS path's terminal failure: the user
    // shared their number, waited the full twenty seconds, and got nothing.
    // `checkLinkedNow` runs the same loop once as a cheap probe, and a probe
    // that finds no link is not a timeout — hence the guard.
    if (attempts > 1) {
        posthog.capture('miniapp_contact_webhook_timeout', {
            platform,
            attempts,
            waited_ms: attempts * delayMs,
        })
    }
    return false
}

/**
 * One immediate check for "has the link appeared yet?".
 *
 * For the person who leaves the mini app, shares their number in the bot chat,
 * and comes back: the webhook linked them while the page sat idle, so the
 * login screen should not still be asking.
 */
export const checkLinkedNow = (): Promise<boolean> => waitForContactLink(1, 0)

/**
 * Bind this Bale account to the member who just authenticated by SMS.
 *
 * Called after a successful login so the *next* open is instant. Failure is
 * silent on purpose: the user is already logged in, and a toast about an
 * invisible optimisation would only confuse them.
 */
export const linkMiniappAccount = async (): Promise<void> => {
    if (!isMiniapp()) return
    const linkTicket = getLinkTicket()
    const initData = getInitData()
    if (!linkTicket && !initData) return

    try {
        await miniappLink({ linkTicket, initData })
        // Cold start just completed — same moment, via the SMS path.
        markFreshLogin()
        // the account is linked now: an invite that brought them counts
        void reportPendingInvite()
        posthog.capture('miniapp_account_linked', {
            platform: getRememberedPlatform(),
        })
    } catch (error) {
        /* next login will try again */
        // Silent to the user by design, but a link that never succeeds means
        // that user pays the SMS toll on every single open — so which of the
        // two proofs we had, and what the backend said, both matter.
        posthog.capture('miniapp_account_link_failed', {
            platform: getRememberedPlatform(),
            status:
                (error as { response?: { status?: number } })?.response
                    ?.status ?? null,
            had_link_ticket: !!linkTicket,
            had_init_data: !!initData,
        })
    }
}
